import axios from "axios";
import FileDropzone from "../../components/FileDropzone";
import { useEffect, useState } from "react";
import { FileText, Plus, ShieldCheck, Sparkles, X, CheckCircle2 } from "lucide-react";
import { useAuth } from "../../auth/AuthContext";
import { API, BACKEND, money, abs, Modal } from "./shared";
import { DesignBriefDrawer } from "./DesignServices";
import { ChatPanel } from "./ChatPanel";
import { DesignJobDeliveryPanel } from "./DesignDelivery";

// ============================================================
// VIEW: DESIGN PROJECTS (buyer or agency)
// ============================================================
export const DESIGN_STATUSES = ["Brief Submitted", "In Design", "Client Proofing", "Approved/Press-Ready"];
export const STATUS_COLORS = {
  "Brief Submitted": "matching",
  "In Design": "live",
  "Client Proofing": "proofing",
  "Approved/Press-Ready": "completed",
};

export function DesignProjects({ role, supplierId, setModal, openJob }) {
  const [items, setItems] = useState([]);
  const [awardedBriefs, setAwardedBriefs] = useState([]);
  const [selected, setSelected] = useState(null);
  const [briefSelected, setBriefSelected] = useState(null);
  const [ref, setRef] = useState(null);
  const { user } = useAuth();
  const load = async () => {
    const params = role === "agency" ? `role=agency&user_id=${supplierId}` : "role=buyer";
    const [dj, briefs] = await Promise.all([
      axios.get(`${API}/design-jobs?${params}`).then(r => r.data).catch(() => []),
      axios.get(`${API}/design/briefs`).then(r => (r.data.briefs || []).filter(b =>
        ["in_progress", "review", "delivered", "approved", "closed", "completed"].includes(b.status)
      )).catch(() => []),
    ]);
    setItems(dj);
    setAwardedBriefs(briefs);
  };
  useEffect(() => {
    axios.get(`${API}/design/reference`).then(r => setRef(r.data)).catch(() => setRef({specialties:[],tiers:[],categories:[],service_types:[]}));
  }, []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [role, supplierId]);

  return (
    <section className="content">
      <div className="section-heading">
        <div>
          <p className="eyebrow">THREE-SIDED MARKETPLACE</p>
          <h2>{role === "agency" ? "Your design queue" : "Design projects"}</h2>
          <p className="subhead">
            {role === "agency"
              ? "Briefs waiting for a quote, in design, or in proofing. Approved artwork releases the printer auction."
              : "Track every brief from submission through 3D proofing. Approving press-ready art auto-releases a printer auction."}
          </p>
        </div>
        {role === "buyer" && (
          <button data-testid="new-brief-button" className="primary-btn" onClick={() => setModal("brief")}>
            <Plus size={16}/> Submit a brief
          </button>
        )}
      </div>

      {awardedBriefs.length > 0 && (
        <div data-testid="awarded-briefs-list" style={{marginBottom:22}}>
          <div className="drawer-section-title" style={{marginTop:0}}>
            <h3>Active briefs with locked escrow</h3>
            <span>{awardedBriefs.length} in flight</span>
          </div>
          <div className="brief-grid" style={{marginTop:12}}>
            {awardedBriefs.map(b => (
              <button
                key={b.id}
                data-testid={`awarded-brief-${b.id}`}
                className="brief-card"
                onClick={() => setBriefSelected(b)}
                style={{textAlign:"left",cursor:"pointer"}}
              >
                <div className="brief-card-top">
                  <span className={`status ${b.status === "in_progress" ? "matching" : b.status === "review" ? "live" : "completed"}`}>{b.status.replace(/_/g," ")}</span>
                  <span style={{font:"10px 'DM Mono'",color:"#94a3b8"}}>{b.hire_mode === "fixed" ? "FIXED HIRE" : "BIDDING"}</span>
                </div>
                <h3>{b.title}</h3>
                <p>{b.awarded_agency_name || "Agency assigned"}</p>
                <div className="brief-details">
                  {b.awarded_price_inr ? <span>✅ {money(b.awarded_price_inr)}</span> : null}
                  {b.awarded_delivery_days ? <span>⏱ {b.awarded_delivery_days} days</span> : null}
                  {b.escrow_status === "held" ? <span>🔒 Escrow locked</span> : null}
                </div>
                <div className="brief-footer">
                  <em>Awarded {b.awarded_at ? new Date(b.awarded_at).toLocaleDateString("en-IN", { timeZone: "Asia/Kolkata" }) : "—"}</em>
                  <span>Open →</span>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="pipeline-grid" data-testid="design-pipeline">
        {DESIGN_STATUSES.map((s) => {
          const col = items.filter(it => it.status === s);
          return (
            <div key={s} className="pipeline-column">
              <div className="pipeline-head">
                <span className={`status ${STATUS_COLORS[s]}`}>{s}</span>
                <b>{col.length}</b>
              </div>
              <div className="pipeline-body">
                {col.length === 0 ? <div className="pipeline-empty">—</div> : col.map(dj => (
                  <button key={dj.id} data-testid={`design-card-${dj.id}`} className="pipeline-card" onClick={() => setSelected(dj)}>
                    <b>{dj.title}</b>
                    <span>{dj.brand_name}</span>
                    <div className="pipeline-meta">
                      {dj._agency?.name ? <em>{dj._agency.name}</em> : <em>Unassigned</em>}
                      {dj.quoted_price ? <span>{money(dj.quoted_price)}</span> : null}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {selected && (
        <DesignJobDrawer
          dj={selected}
          role={role}
          supplierId={supplierId}
          close={() => setSelected(null)}
          refresh={load}
          openJob={openJob}
        />
      )}
      {briefSelected && ref && (
        <DesignBriefDrawer
          brief={briefSelected}
          ref_={ref}
          role={role}
          userId={user?.id}
          user={user}
          close={() => setBriefSelected(null)}
          refresh={load}
        />
      )}
    </section>
  );
}

export function DesignJobDrawer({ dj: initial, role, supplierId, close, refresh, openJob }) {
  const { user } = useAuth();
  const [dj, setDj] = useState(initial);
  const [artworkFiles, setArtworkFiles] = useState([]);
  const [quotePrice, setQuotePrice] = useState(initial.quoted_price || 1500);
  const reload = async () => {
    const { data } = await axios.get(`${API}/design-jobs/${initial.id}`);
    setDj(data);
  };
  useEffect(() => { reload().catch(() => {}); }, [initial.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const assign = async () => {
    await axios.post(`${API}/design-jobs/${dj.id}/assign`, { agency_id: supplierId, quoted_price: Number(quotePrice) });
    await reload();
    refresh();
  };
  const submitArtwork = async () => {
    await axios.post(`${API}/design-jobs/${dj.id}/artwork`, {
      artwork_file_ids: artworkFiles.map(f => f.id),
      artwork_urls: [],
      proof_url: artworkFiles[0]?.url ? `${BACKEND}${artworkFiles[0].url}` : null,
      notes: "",
    });
    setArtworkFiles([]);
    await reload();
    refresh();
  };
  const approve = async () => {
    if (!window.confirm("Approve press-ready artwork? This will auto-create a printer RFQ.")) return;
    const { data } = await axios.post(`${API}/design-jobs/${dj.id}/approve`);
    await reload();
    refresh();
    if (data?.linked_job_id) alert(`RFQ auction created: ${data.linked_job_id}`);
  };

  return (
    <div className="drawer-backdrop" onClick={close}>
      <aside className="drawer" onClick={e => e.stopPropagation()}>
        <button data-testid="dj-drawer-close" className="close-btn" onClick={close}><X size={18}/></button>
        <div className="drawer-top">
          <p className="eyebrow">DESIGN PROJECT · {dj.id}</p>
          <h2>{dj.title}</h2>
          <div className="drawer-status-row">
            <span className={`status ${STATUS_COLORS[dj.status] || "live"}`}>{dj.status}</span>
            {dj.linked_job_id && <span className="status completed">RFQ · {dj.linked_job_id}</span>}
          </div>
        </div>

        <div className="detail-block">
          <label>Brand</label><b>{dj.brand_name}</b>
          <label>Direction</label><p>{dj.style}</p>
          <label>Production</label><p>{dj.dimensions} · {dj.substrate}</p>
          {dj.notes && <><label>Notes</label><p>{dj.notes}</p></>}
          {dj._agency && <><label>Agency</label><b>{dj._agency.name}</b></>}
          {dj.quoted_price && <><label>Design fee</label><b>{money(dj.quoted_price)}</b></>}
        </div>

        {/* AGENCY: claim + quote */}
        {role === "agency" && dj.status === "Brief Submitted" && !dj.assigned_agency_id && (
          <>
            <div className="drawer-section-title"><h3>Send a quote</h3><span>Claim this brief</span></div>
            <div className="quote-row">
              <input data-testid="quote-price-input" type="number" value={quotePrice} onChange={e => setQuotePrice(e.target.value)} min="1"/>
              <button data-testid="agency-claim-button" className="primary-btn" onClick={assign}>Claim & quote <Sparkles size={15}/></button>
            </div>
          </>
        )}

        {/* AGENCY: upload artwork when In Design */}
        {role === "agency" && dj.assigned_agency_id === supplierId && dj.status === "In Design" && (
          <>
            <div className="drawer-section-title"><h3>Deliver artwork</h3><span>Upload PDF + dieline</span></div>
            <FileDropzone
              testid="artwork-deliver-dropzone"
              scope="artwork"
              accept="application/pdf,image/*,.dxf,.ai,.eps,.zip"
              value={artworkFiles}
              onChange={setArtworkFiles}
              label="Drop the press-ready files"
              hint="PDF, DXF, AI, EPS or ZIP — sent to the buyer for 3D proofing"
            />
            <button data-testid="agency-submit-artwork" className="primary-btn full" onClick={submitArtwork} disabled={!artworkFiles.length}>
              Send for buyer proofing <Sparkles size={15}/>
            </button>
          </>
        )}

        {/* BUYER: proof view + approve */}
        {role === "buyer" && dj.status === "Client Proofing" && (
          <>
            <div className="drawer-section-title"><h3>3D soft-proof</h3><span>Review before releasing to printers</span></div>
            {dj.proof_url && (
              <div className="proof-preview">
                <img src={abs(dj.proof_url)} alt="soft proof" onError={(e) => { e.currentTarget.style.display = "none"; }}/>
                <div className="proof-caption"><b>{dj.title}</b><span>Delivered by {dj._agency?.name}</span></div>
              </div>
            )}
            {(dj.completed_artwork_urls || []).length > 0 && (
              <div className="asset-list" data-testid="design-artwork-list">
                {dj.completed_artwork_urls.map((u, i) => (
                  <a key={i} href={abs(u)} target="_blank" rel="noreferrer" className="asset-row">
                    <div className="asset-icon"><FileText size={16}/></div>
                    <div className="asset-info"><b>{u.split("/").pop()}</b><span>Ready for printer release</span></div>
                    <span className="asset-download">Download ↗</span>
                  </a>
                ))}
              </div>
            )}
            <button data-testid="approve-design-button" className="primary-btn full" onClick={approve}>
              Approve press-ready & open printer RFQ <ShieldCheck size={15}/>
            </button>
          </>
        )}

        {/* Approved: link to the resulting RFQ */}
        {dj.status === "Approved/Press-Ready" && dj.linked_job_id && (
          <div className="drawer-callout ok" data-testid="rfq-linked-callout">
            <CheckCircle2 size={17}/>
            <div>
              <b>Press-ready artwork approved</b>
              <span>Printer RFQ <b>{dj.linked_job_id}</b> has been queued for admin review.</span>
            </div>
          </div>
        )}

        {/* NDA + Final delivery for the legacy design-job kanban. Mirrors the
            same panel buyers see on the newer design-briefs flow. */}
        {dj.assigned_agency_id && (
          <DesignJobDeliveryPanel dj={dj} role={role} userId={user?.id} onChanged={reload}/>
        )}

        {dj.assigned_agency_id && (
          <ChatPanel scopeType="design_job" scopeId={dj.id} role={role}/>
        )}
      </aside>
    </div>
  );
}

export function DesignJobModal({ close, refresh }) {
  const [f, setF] = useState({
    brand_name: "", title: "", dimensions: "", substrate: "", style: "",
    logo_status: "Logo ready", notes: "", required_capabilities: [], budget_usd: 1500,
  });
  const [busy, setBusy] = useState(false);
  const [matches, setMatches] = useState([]);
  const [chosen, setChosen] = useState(null);
  const change = e => setF({ ...f, [e.target.name]: e.target.value });
  const toggleCap = (c) => setF(v => ({
    ...v,
    required_capabilities: v.required_capabilities.includes(c) ? v.required_capabilities.filter(x => x !== c) : [...v.required_capabilities, c],
  }));

  // Live-fetch top 3 as user tweaks capabilities/budget
  useEffect(() => {
    const caps = f.required_capabilities.join(",");
    const budget = Number(f.budget_usd) || 1500;
    const t = setTimeout(() => {
      axios.get(`${API}/design-jobs/match?required_capabilities=${encodeURIComponent(caps)}&budget_usd=${budget}`)
        .then(r => setMatches(r.data))
        .catch(() => setMatches([]));
    }, 250);
    return () => clearTimeout(t);
  }, [f.required_capabilities, f.budget_usd]);

  const submit = async e => {
    e.preventDefault();
    setBusy(true);
    await axios.post(`${API}/design-jobs`, {
      ...f,
      budget_usd: Number(f.budget_usd),
      selected_agency_id: chosen?.user_id || null,
      buyer_id: "buyer-1",
      buyer_email: "buyer@northstargoods.com",
    });
    setBusy(false);
    await refresh();
    close();
  };
  return (
    <Modal title="Submit an artwork brief" close={close}>
      <form onSubmit={submit} className="form-grid">
        {[["brand_name", "Brand name"], ["title", "Brief title"], ["dimensions", "Pack dimensions"], ["substrate", "Preferred substrate"], ["style", "Visual direction"]].map(x => (
          <label key={x[0]}>{x[1]}
            <input data-testid={`brief-${x[0]}-input`} name={x[0]} value={f[x[0]]} onChange={change} required/>
          </label>
        ))}
        <label>Logo status
          <select data-testid="brief-logo-select" name="logo_status" value={f.logo_status} onChange={change}>
            <option>Logo ready</option><option>Needs logo direction</option>
          </select>
        </label>
        <label>Design budget (INR)
          <input data-testid="brief-budget-input" name="budget_usd" type="number" min="100" step="50" value={f.budget_usd} onChange={change}/>
        </label>
        <label style={{ gridColumn: "span 2" }}>Notes for agencies
          <textarea data-testid="brief-notes-input" name="notes" value={f.notes} onChange={change} placeholder="Any brand references, mood, must-haves…"/>
        </label>
        <div className="modal-full-row">
          <label className="dz-label">Required capabilities</label>
          <div className="cap-pills">
            {["3D Dieline CAD", "Graphic Layout", "Branding", "Labeling"].map(c => (
              <button
                type="button"
                key={c}
                data-testid={`cap-${c.replace(/\s+/g, "-").toLowerCase()}`}
                className={`cap-pill ${f.required_capabilities.includes(c) ? "active" : ""}`}
                onClick={() => toggleCap(c)}
              >{c}</button>
            ))}
          </div>
        </div>

        {matches.length > 0 && (
          <div className="modal-full-row">
            <label className="dz-label">Top matches · algorithmic rank</label>
            <div className="match-list" data-testid="match-list">
              {matches.map((m, i) => {
                const active = chosen?.user_id === m.user_id;
                return (
                  <button
                    key={m.id}
                    type="button"
                    data-testid={`match-card-${i}`}
                    className={`match-card ${active ? "active" : ""}`}
                    onClick={() => setChosen(active ? null : m)}
                  >
                    <div className="match-rank">#{i + 1}</div>
                    <div className="match-info">
                      <b>{m._user?.name} <span className={`tier-badge tier-${m.subscription_tier?.toLowerCase()}`}>{m.subscription_tier}</span></b>
                      <span className="match-tagline">{m.tagline}</span>
                      <div className="match-caps">{(m.design_capabilities || []).map(c => <em key={c}>{c}</em>)}</div>
                    </div>
                    <div className="match-score">
                      <strong>{m._score.total_score}</strong>
                      <small>score</small>
                      <div className="match-breakdown">
                        <span title="Capability match">C {m._score.capability_score}</span>
                        <span title="Budget fit">B {m._score.budget_score}</span>
                        <span title="Approval rate">A {m._score.approval_score}</span>
                      </div>
                      <div className="match-rate">{money(m.base_rate_usd)}</div>
                    </div>
                  </button>
                );
              })}
            </div>
            {chosen && (
              <div className="drawer-callout" data-testid="escrow-notice">
                <ShieldCheck size={16}/>
                <div>
                  <b>{money(chosen.base_rate_usd)} escrow will be captured on submission</b>
                  <span>Funds are held via Razorpay escrow until you approve the press-ready artwork.</span>
                </div>
              </div>
            )}
          </div>
        )}

        <div className="form-actions">
          <button data-testid="brief-cancel-button" type="button" className="secondary-btn" onClick={close}>Cancel</button>
          <button data-testid="brief-submit-button" className="primary-btn" disabled={busy}>{busy ? "Sending…" : (chosen ? "Capture escrow & assign" : "Send to matching agencies")} <Sparkles size={15}/></button>
        </div>
      </form>
    </Modal>
  );
}
